import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api, setCsrfToken } from '@/lib/api';
import { initials } from '@/lib/format';
import { ME_KEY, useMe } from '@/lib/queries';

/** Signs out and returns to the login page (used by the account menu and Settings). */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      setCsrfToken(null);
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, null);
      void navigate({ to: '/login', search: {} });
    },
  });
}

export function UserMenu() {
  const me = useMe();
  const logout = useLogout();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="text-sidebar-foreground hover:bg-sidebar-accent h-9 min-w-0 flex-1 justify-start gap-2 rounded-[10px] px-1.5 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:px-1"
          aria-label="Account menu"
        >
          <Avatar className="size-6">
            <AvatarFallback className="text-[10px]">
              {initials(me.user.name, me.user.email)}
            </AvatarFallback>
          </Avatar>
          <span className="truncate text-sm font-medium group-data-[collapsible=icon]:hidden">
            {me.user.name || me.user.email}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm font-medium">{me.user.name || me.user.email}</div>
          <div className="text-muted-foreground truncate text-xs">{me.user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout.mutate()} disabled={logout.isPending}>
          <LogOut className="size-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
